<template>
  <section id="product-batch-price-table">
    <b-table
      :data="items"
      :mobile-cards="false"
      narrowed
      sticky-header
      :row-class="rowClass"
      :height="height"
    >
      <b-table-column label="" width="40" centered v-slot="props">
        <b-tooltip v-show="props.row.error" position="is-right">
          <b-icon icon="information" type="is-danger"></b-icon>
          <template v-slot:content>
            {{ props.row.error }}
          </template>
        </b-tooltip>
      </b-table-column>

      <b-table-column
        field="code"
        :label="$t('attributes.product_code')"
        width="100"
        centered
        sortable
        v-slot="props"
      >
        <b-tooltip type="is-light" position="is-right">
          <p>{{ props.row.code }}</p>
          <template v-slot:content>
            <p v-html="props.row.name"></p>
          </template>
        </b-tooltip>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.deposit')"
        width="240"
        centered
        v-slot="props"
      >
        <b-input
          :ref="`deposit-input-${props.row.code}`"
          v-model.number="props.row.deposit"
          name="deposit"
          type="number"
          min="0"
          :use-html5-validation="false"
          :placeholder="$t('message.please_input') + $t('attributes.deposit')"
          @input="depositModified(props.row)"
          @keypress.enter.exact.native.prevent="
            finish(props.row, props.index + 1, 'deposit')
          "
          @keypress.enter.shift.exact.native.prevent="
            finish(props.row, props.index - 1, 'deposit')
          "
        ></b-input>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.hide_price')"
        width="100"
        centered
        v-slot="props"
      >
        <b-field>
          <b-radio-button
            v-model="props.row.hide_price"
            :name="`hide_price-${props.row.code}`"
            native-value="N"
            type="is-success is-light is-outlined"
            @input="modified(props.row)"
          >
            <span>{{ $t('attributes.display') }}</span>
          </b-radio-button>
          <b-radio-button
            v-model="props.row.hide_price"
            :name="`hide_price-${props.row.code}`"
            native-value="Y"
            type="is-danger is-light is-outlined"
            @input="modified(props.row)"
          >
            <span>{{ $t('attributes.hide') }}</span>
          </b-radio-button>
        </b-field>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.show_up')"
        width="150"
        centered
        v-slot="props"
      >
        <b-field>
          <b-radio-button
            v-model="props.row.show_up"
            :name="`show_up-${props.row.code}`"
            native-value="Y"
            type="is-success is-light is-outlined"
            @input="modified(props.row)"
          >
            <span>{{ $t('attributes.yes') }}</span>
          </b-radio-button>
          <b-radio-button
            v-model="props.row.show_up"
            :name="`show_up-${props.row.code}`"
            native-value="N"
            type="is-danger is-light is-outlined"
            @input="modified(props.row)"
          >
            <span>{{ $t('attributes.no') }}</span>
          </b-radio-button>
        </b-field>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.tip_fee')"
        width="200"
        centered
        v-slot="props"
      >
        <b-field>
          <b-select
            :ref="`tip-flag-input-${props.row.code}`"
            v-model="props.row.tip_flag"
            name="tip_flag"
            @input="modified(props.row)"
          >
            <option value="Y">{{ $t('attributes.included') }}</option>
            <option value="N">{{ $t('attributes.excluded') }}</option>
          </b-select>
          <b-input
            :ref="`tip-fee-input-${props.row.code}`"
            v-model.number="props.row.tip_fee"
            name="tip_fee"
            type="number"
            min="0"
            :use-html5-validation="false"
            :placeholder="$t('message.please_input') + $t('attributes.tip_fee')"
            expanded
            @input="tipFeeModified(props.row)"
            @keypress.enter.exact.native.prevent="
              finish(props.row, props.index + 1, 'tip-fee')
            "
            @keypress.enter.shift.exact.native.prevent="
              finish(props.row, props.index - 1, 'tip-fee')
            "
          ></b-input>
        </b-field>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.tax_fee')"
        width="200"
        centered
        v-slot="props"
      >
        <b-field>
          <b-select
            :ref="`insurance-flag-input-${props.row.code}`"
            v-model="props.row.insurance_flag"
            name="insurance_flag"
            @input="modified(props.row)"
          >
            <option value="Y">{{ $t('attributes.included') }}</option>
            <option value="N">{{ $t('attributes.excluded') }}</option>
          </b-select>
          <b-input
            :ref="`insurance-fee-input-${props.row.code}`"
            v-model.number="props.row.insurance_fee"
            name="insurance_fee"
            type="number"
            min="0"
            :use-html5-validation="false"
            :placeholder="$t('message.please_input') + $t('attributes.tax_fee')"
            expanded
            @input="taxFeeModified(props.row)"
            @keypress.enter.exact.native.prevent="
              finish(props.row, props.index + 1, 'insurance-fee')
            "
            @keypress.enter.shift.exact.native.prevent="
              finish(props.row, props.index - 1, 'insurance-fee')
            "
          ></b-input>
        </b-field>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.visa_fee')"
        width="220"
        centered
        v-slot="props"
      >
        <b-field>
          <b-select
            :ref="`visa-flag-input-${props.row.code}`"
            v-model="props.row.visa_flag"
            name="visa_flag"
            @input="modified(props.row)"
          >
            <option value="Y">{{ $t('attributes.included') }}</option>
            <option value="N">{{ $t('attributes.excluded') }}</option>
            <option value="X">{{ $t('attributes.visa_free') }}</option>
          </b-select>
          <b-input
            :ref="`visa-fee-input-${props.row.code}`"
            v-model.number="props.row.visa_fee"
            name="visa_fee"
            type="number"
            min="0"
            :use-html5-validation="false"
            :placeholder="
              $t('message.please_input') + $t('attributes.visa_fee')
            "
            expanded
            @input="visaFeeModified(props.row)"
            @keypress.enter.exact.native.prevent="
              finish(props.row, props.index + 1, 'visa-fee')
            "
            @keypress.enter.shift.exact.native.prevent="
              finish(props.row, props.index - 1, 'visa-fee')
            "
          ></b-input>
        </b-field>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.discount_limit')"
        width="200"
        centered
        v-slot="props"
      >
        <b-input
          :ref="`discount-limit-input-${props.row.code}`"
          v-model.number="props.row.discount_limit"
          name="discount_limit"
          type="number"
          min="0"
          :use-html5-validation="false"
          :placeholder="
            $t('message.please_input') + $t('attributes.discount_limit')
          "
          @input="discountModified(props.row)"
          @keypress.enter.exact.native.prevent="
            finish(props.row, props.index + 1, 'discount-limit')
          "
          @keypress.enter.shift.exact.native.prevent="
            finish(props.row, props.index - 1, 'discount-limit')
          "
        ></b-input>
      </b-table-column>

      <b-table-column
        width="50"
        :label="$t('attributes.function')"
        centered
        v-slot="props"
      >
        <div class="buttons is-centered">
          <b-tooltip :label="$t('attributes.restore')">
            <b-button
              type="is-info"
              icon-left="restore"
              outlined
              @click.prevent="restore(props.row)"
            >
            </b-button>
          </b-tooltip>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section" style="height: 100%">
          <div v-if="!loading" class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>
  </section>
</template>

<script>
export default {
  name: 'ProductBatchPriceTable',

  props: {
    products: {
      Type: Array,
      required: true
    },
    height: Number,
    loading: Boolean
  },
  data() {
    return {
      items: this.extractProducts(this.products)
    }
  },
  watch: {
    products(list) {
      this.items = this.extractProducts(list)
    }
  },
  methods: {
    extractProducts(products) {
      return products.map(product => {
        return {
          code: product.code,
          name: product.name,
          deposit: product.deposit || 0,
          tip_flag: product.tip_flag ? product.tip_flag.trim() : 'N',
          tip_fee: product.tip_fee || 0,
          insurance_flag: product.insurance_flag
            ? product.insurance_flag.trim()
            : 'N',
          insurance_fee: product.insurance_fee || 0,
          visa_flag: product.visa_flag ? product.visa_flag.trim() : 'N',
          visa_fee: product.visa_fee || 0,
          hide_price: product.hide_price ? product.hide_price.trim() : 'N',
          show_up: product.show_up ? product.show_up.trim() : 'N',
          discount_limit: product.discount_limit,

          editing: false,
          dirty: false,
          error: null,
          origin: {
            ...product,
            deposit: product.deposit || 0,
            tip_flag: product.tip_flag ? product.tip_flag.trim() : 'N',
            tip_fee: product.tip_fee || 0,
            insurance_flag: product.insurance_flag
              ? product.insurance_flag.trim()
              : 'N',
            insurance_fee: product.insurance_fee || 0,
            visa_flag: product.visa_flag ? product.visa_flag.trim() : 'N',
            visa_fee: product.visa_fee || 0,
            hide_price: product.hide_price ? product.hide_price.trim() : 'N',
            show_up: product.show_up ? product.show_up.trim() : 'N',
            discount_limit: product.discount_limit
          }
        }
      })
    },
    depositModified(product) {
      const deposit = parseInt(product.deposit)

      if (isNaN(deposit)) {
        product.deposit = 0
      }

      this.modified(product)
    },
    tipFeeModified(product) {
      const fee = parseInt(product.tip_fee)

      if (isNaN(fee)) {
        product.tip_fee = 0
      }

      this.modified(product)
    },
    taxFeeModified(product) {
      const fee = parseInt(product.insurance_fee)

      if (isNaN(fee)) {
        product.insurance_fee = 0
      }

      this.modified(product)
    },
    visaFeeModified(product) {
      const fee = parseInt(product.visa_fee)

      if (isNaN(fee)) {
        product.visa_fee = 0
      }

      this.modified(product)
    },
    discountModified(product) {
      const discount = parseInt(product.discount_limit)

      if (isNaN(discount)) {
        product.discount_limit = 0
      }

      this.modified(product)
    },
    finish(product, next, field) {
      this.modified(product)

      let index = (next + this.items.length) % this.items.length
      if (index >= 0 && index < this.items.length) {
        const nextProduct = this.items[index]

        setTimeout(() => {
          const name = `${field}-input-${nextProduct.code}`
          const input = this.$refs[name].getElement()
          if (input) {
            input.focus()
          }
        }, 0)
      }
    },
    modified(product) {
      product.dirty =
        product.deposit !== product.origin.deposit ||
        product.hide_price !== product.origin.hide_price ||
        product.show_up !== product.origin.show_up ||
        product.discount_limit !== product.origin.discount_limit ||
        product.tip_flag !== product.origin.tip_flag ||
        product.tip_fee !== product.origin.tip_fee ||
        product.insurance_flag !== product.origin.insurance_flag ||
        product.insurance_fee !== product.origin.insurance_fee ||
        product.visa_flag !== product.origin.visa_flag ||
        product.visa_fee !== product.origin.visa_fee

      this.validate(product)
    },
    restore(product) {
      product.deposit = product.origin.deposit
      product.tip_flag = product.origin.tip_flag
      product.tip_fee = product.origin.tip_fee
      product.insurance_flag = product.origin.insurance_flag
      product.insurance_fee = product.origin.insurance_fee
      product.visa_flag = product.origin.visa_flag
      product.visa_fee = product.origin.visa_fee
      product.hide_price = product.origin.hide_price
      product.show_up = product.origin.show_up
      product.discount_limit = product.origin.discount_limit

      product.dirty = false
      product.error = false
    },
    isDirty() {
      return this.items.filter(x => x.dirty).length > 0
    },
    isEditing() {
      return this.items.filter(x => x.editing).length > 0
    },
    isValidate() {
      for (const product of this.items) {
        if (!this.validate(product)) {
          return false
        }
      }

      return true
    },
    validate(product) {
      if (product.deposit < 0) {
        product.error = '訂金不可小於0'
        return false
      }
      if (product.tip_fee < 0) {
        product.error = '小費不可小於0'
        return false
      }
      if (product.insurance_fee < 0) {
        product.error = '稅金不可小於0'
        return false
      }
      if (product.visa_fee < 0) {
        product.error = '簽證費不可小於0'
        return false
      }
      if (product.discount_limit < 0) {
        product.error = '團費折抵上限不可小於0'
        return false
      }

      product.error = null
      return true
    },
    getData() {
      return this.items
        .filter(product => product.dirty)
        .map(product => ({
          code: product.code,
          deposit: product.deposit,
          hide_price: product.hide_price,
          show_up: product.show_up,
          discount_limit: product.discount_limit,
          tip_flag: product.tip_flag,
          tip_fee: product.tip_fee,
          insurance_flag: product.insurance_flag,
          insurance_fee: product.insurance_fee,
          visa_flag: product.visa_flag,
          visa_fee: product.visa_fee
        }))
    },
    rowClass(product) {
      if (product.error) {
        return 'has-background-danger-light'
      } else if (product.dirty) {
        return 'has-background-warning'
      } else {
        return null
      }
    }
  }
}
</script>

<style lang="scss">
#product-batch-price-table {
  .b-table .table {
    min-width: 1480px;
  }
}
</style>
